import type { ISPD } from '../../sprites/sprites'
import { sprites } from '../../sprites/sprites-sidecar'
import type { IMessageWeaponCommand } from './multi/voyageMessageWeaponCommand'
import { voyageColors } from './voyageColors-sidecar'
import type { IVoyageCrew } from './voyageCrew'
import type { IVoyageSystemState } from './voyageSystems'
import { voyageSystems_bed } from './voyageSystems_bed-sidecar'
import { voyageSystems_coffeeMaker } from './voyageSystems_coffeeMaker-sidecar'
import { voyageSystems_coms } from './voyageSystems_coms-sidecar'
import { voyageSystems_crematorium } from './voyageSystems_crematorium-sidecar'
import { voyageSystems_cryo } from './voyageSystems_cryo-sidecar'
import { voyageSystems_ejector } from './voyageSystems_ejector-sidecar'
import { voyageSystems_ejectrol } from './voyageSystems_ejectrol-sidecar'
import { voyageSystems_externalSensor } from './voyageSystems_externalSensor-sidecar'
import { voyageSystems_hyperdrive } from './voyageSystems_hyperdrive-sidecar'
import { voyageSystems_internalSensor } from './voyageSystems_internalSensor-sidecar'
import { voyageSystems_laser } from './voyageSystems_laser-sidecar'
import { voyageSystems_lifeSupport } from './voyageSystems_lifeSupport-sidecar'
import { voyageSystems_med } from './voyageSystems_med-sidecar'
import { voyageSystems_medPad } from './voyageSystems_medPad-sidecar'
import { voyageSystems_nav } from './voyageSystems_nav-sidecar'
import { voyageSystems_rack } from './voyageSystems_rack-sidecar'
import { voyageSystems_reactor } from './voyageSystems_reactor-sidecar'
import { voyageSystems_torpedo } from './voyageSystems_torpedo-sidecar'
import { voyageSystems_torpedoLoader } from './voyageSystems_torpedoLoader-sidecar'
import { EVoyageTile } from './voyageTileData'
import type { IVoyageTilemapPart } from './voyageTilemapPart'

export enum EVoyageSystemType {
  Null,
  NotMarked,
  Nav,
  SensorInternal,
  SensorExternal,
  Coms,
  TorpedoLoader,
  LifeSupport,
  Reactor,
  JumpDrive,
}

export interface IVoyageSystemData {
  type: EVoyageSystemType
  startsWith: string
  tileType: EVoyageTile
  frame: ISPD | null
  onCreate: (
    sys: IVoyageSystemState,
    text3: string,
    t: IVoyageTilemapPart,
  ) => void
  crewUpdate: (obj: IVoyageCrew) => void
  onEnter?: (obj: IVoyageCrew, isFinalMove: boolean) => void
  onLeave?: (obj: IVoyageCrew) => void
  onSelectedCrewOn?: (obj: IVoyageCrew) => void
  networkProcessWeaponCommand?: (
    msg: IMessageWeaponCommand,
    obj: IVoyageCrew,
    t: IVoyageTilemapPart,
    sys: IVoyageSystemState,
  ) => void
  startsWithAmmo: number
  abbv: string
  abbvXAdjust: number
  infoText: string
  name: string
  extraInfo?: (t: IVoyageTilemapPart) => string
  frozenCrewName?: string
  allowMultiple: boolean

  killText: boolean
  killImage: boolean
  hide: boolean
  tint: number
}

const _default: IVoyageSystemData = {
  type: EVoyageSystemType.NotMarked,
  name: 'Default',
  startsWith: 'default',
  tileType: EVoyageTile.System,
  onCreate: () => {},
  crewUpdate: () => {},
  onSelectedCrewOn: () => {},
  startsWithAmmo: 0,
  abbv: '',
  abbvXAdjust: 0,
  infoText: '',
  frozenCrewName: '',
  allowMultiple: false,
  frame: null,
  killText: false,
  killImage: false,
  hide: false,
  tint: 0xffffff,
}

let dataList: IVoyageSystemData[] = []

export function register(data: Partial<IVoyageSystemData>) {
  let d = Object.assign({}, _default, data)
  dataList.push(d)
  return d
}

export function find(tile: IVoyageTilemapPart) {
  return findBy(tile.tile3.type, tile.text3)
}

export function findBy(tileType: EVoyageTile, text3: string) {
  for (let i = 0; i < dataList.length; i++) {
    let c = dataList[i]
    if (c.tileType !== tileType) {
      continue
    }
    if (c.startsWith) {
      if (!text3.startsWith(c.startsWith)) {
        continue
      }
    }
    return c
  }

  return _default
}

export function registerAll() {
  register({
    startsWith: 'null',
    name: 'Null',
  })

  register({
    startsWith: 'cryo',
    onCreate: voyageSystems_cryo.onCreate,
    crewUpdate: voyageSystems_cryo.crewUpdate,
    startsWithAmmo: 1,
    abbv: 'Y',
    name: 'Cryo Freezing Station',
    infoText: 'Often contains emergency additional crew in a popsicle state.',
    extraInfo: (t) => {
      if (t.system) {
        if (t.system.ammo === 0) {
          return 'Is currently empty'
        }
        if (t.system.ammo === 1) {
          return 'Contains ' + t.system.frozenCrewName
        }
      }
      return '?'
    },
  })

  register({
    type: EVoyageSystemType.SensorInternal,
    startsWith: 'sensor-internal',
    crewUpdate: voyageSystems_internalSensor.crewUpdate,
    onLeave: voyageSystems_internalSensor.onLeave,
    onSelectedCrewOn: voyageSystems_internalSensor.onSelectedCrewOn,
    abbv: 'SI',
    name: 'Internal Sensors',
    infoText: 'Scan for anomalies inside the ship.',
  })

  register({
    type: EVoyageSystemType.SensorExternal,
    startsWith: 'sensor-external',
    crewUpdate: voyageSystems_externalSensor.crewUpdate,
    onLeave: voyageSystems_externalSensor.onLeave,
    onSelectedCrewOn: voyageSystems_externalSensor.onSelectedCrewOn,
    abbv: 'SE',
    name: 'External Sensors',
    infoText:
      'Scan for anomalies ouside the ship using radar, lidar, and truthdar.',
  })

  register({
    startsWith: 'med',
    crewUpdate: voyageSystems_med.crewUpdate,
    abbv: 'M',
    abbvXAdjust: -3,
    name: 'Medical Station',
    infoText: 'Enable healing on the nearby Medical Pad.',
  })

  register({
    tileType: EVoyageTile.MedPad,
    startsWith: 'pad',
    crewUpdate: voyageSystems_medPad.crewUpdate,
    name: 'Medical Pad',
    infoText:
      'Stand here to heal, if there is a crewmember operating the nearby Medical Station.',
    killText: true,
  })

  register({
    tileType: EVoyageTile.Elevator,
    startsWith: '',
    //crewUpdate: voyageSystems_med.crewUpdate,
    name: 'Elevator',
    infoText: 'Moves crew between different floors on the Dawn Star.',
    killText: true,
  })

  register({
    type: EVoyageSystemType.Reactor,
    startsWith: 'reactor',
    crewUpdate: voyageSystems_reactor.crewUpdate,
    onLeave: voyageSystems_reactor.onLeave,
    onSelectedCrewOn: voyageSystems_reactor.onSelectedCrewOn,
    allowMultiple: true,
    abbv: 'R',
    name: 'Reactor',
    infoText:
      'Powers the ship, a critical component that you do not want to go critical.',
  })

  register({
    type: EVoyageSystemType.LifeSupport,
    startsWith: 'life-support',
    crewUpdate: voyageSystems_lifeSupport.crewUpdate,
    onLeave: voyageSystems_lifeSupport.onLeave,
    onSelectedCrewOn: voyageSystems_lifeSupport.onSelectedCrewOn,
    allowMultiple: true,
    abbv: 'LS',
    name: 'Life Support',
    infoText:
      'Supports life by providing oxygen to fragile crew members. Does not help robots.',
  })

  register({
    startsWith: 'crematorium',
    crewUpdate: voyageSystems_crematorium.crewUpdate,
    abbv: 'CM',
    name: 'Crematorium',
    abbvXAdjust: -3,
    infoText:
      'In space, no one can hear you scream.\n\nTrying to find a safe way to dispose of dead bodies?\n\nIn space, no one can. Here, use crematorium.\n\nWarning: starts corpses on fire!',
  })

  register({
    startsWith: 'laser',
    crewUpdate: voyageSystems_laser.crewUpdate,
    networkProcessWeaponCommand:
      voyageSystems_laser.networkProcessWeaponCommand,

    abbv: 'L',
    name: 'Laser Cannon',
    infoText:
      'Fast and easy to use laser cannon (tm). Beware, it takes quite a toll on the ship power.',
  })

  register({
    startsWith: 'torpedo',
    crewUpdate: voyageSystems_torpedo.crewUpdate,
    networkProcessWeaponCommand:
      voyageSystems_torpedo.networkProcessWeaponCommand,
    abbv: 'T',
    name: 'Torpedo Launcher',
    infoText:
      'Fire highly explosive torpedoes. Rumors about the ammunition being highly-refined potatoes are unfounded. Requires a crew to first load a torpedo in the nearby Torpedo Loader.',
    extraInfo: (t) => {
      if (t.system) {
        if (t.system.ammo === 0) {
          return 'No torpedo is loaded'
        } else {
          return (
            'Contains ' +
            t.system.ammo +
            ' fully-armed torpedo' +
            (t.system.ammo === 1 ? '' : 's')
          )
        }
      }
      return '?'
    },
  })

  register({
    type: EVoyageSystemType.TorpedoLoader,
    startsWith: 'loader',
    crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    onLeave: voyageSystems_torpedoLoader.onLeave,
    onSelectedCrewOn: voyageSystems_torpedoLoader.onSelectedCrewOn,
    abbv: 'TL',
    name: 'Torpedo Loader',
    infoText:
      'Careful! These things are expensive and heavy. Load a highly-refined potato-based exposive into the nearby Torpedo Launcher tube.',
  })

  register({
    startsWith: 'bed',
    crewUpdate: voyageSystems_bed.crewUpdate,
    abbv: 'B',
    name: 'Bed',
    infoText:
      'If you do not know what one of these are for, you are probably an alien impostor.',
    allowMultiple: true,
    frame: sprites.voyTileBed[0],
    killText: true,
    killImage: true,
  })

  register({
    startsWith: 'captain',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'C',
    name: 'The Chair of the Captain',
    infoText: 'Does not do much but it sure looks comfortable.',
  })

  register({
    startsWith: 'galley',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'G',
  })

  register({
    startsWith: 'engine',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'E',
  })

  register({
    startsWith: 'shield',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'S',
  })

  register({
    type: EVoyageSystemType.Nav,
    startsWith: 'nav',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    onEnter: voyageSystems_nav.onEnter,
    onLeave: voyageSystems_nav.onLeave,
    onSelectedCrewOn: voyageSystems_nav.onSelectedCrewOn,
    name: 'Nav',
    abbv: 'N',
    abbvXAdjust: -2,
    infoText: 'Plot a course to a new destination',
  })

  register({
    type: EVoyageSystemType.Coms,
    startsWith: 'coms',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    onEnter: voyageSystems_coms.onEnter,
    onLeave: voyageSystems_coms.onLeave,
    onSelectedCrewOn: voyageSystems_coms.onSelectedCrewOn,
    name: 'Coms',
    abbv: 'CO',
    infoText: 'Order supplies and crew from nearby space stations',
    // abbvXAdjust: -2,
  })

  register({
    type: EVoyageSystemType.JumpDrive,
    startsWith: 'hyper',
    crewUpdate: voyageSystems_hyperdrive.crewUpdate,
    onLeave: voyageSystems_hyperdrive.onLeave,
    onSelectedCrewOn: voyageSystems_hyperdrive.onSelectedCrewOn,
    //onEnter: voyageSystems_coms.onEnter,
    name: 'Hyper-X FTL Drive',
    abbv: 'XD',
    infoText: 'Charge so Nav can jump to a new location',
    // abbvXAdjust: -2,
  })

  register({
    startsWith: 'cargo',
    allowMultiple: true,
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    name: 'Cargo bay',
    abbv: 'CB',
    killText: true,
    frame: sprites.voyTileSquareInv[0],
    tint: voyageColors.purple,
    // abbvXAdjust: -2,
  })
  register({
    startsWith: 'tele',
    allowMultiple: true,
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    name: 'Teleporter',
    abbv: 'TP',
    frame: sprites.voyTileSquareRounded[0],
    tint: voyageColors.green,
    // abbvXAdjust: -2,
  })

  register({
    startsWith: 'loading',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'LB',
    name: 'Loading bay',
    infoText: '',
  })

  register({
    startsWith: 'coffe',
    onEnter: voyageSystems_coffeeMaker.onEnter,
    abbv: 'CF',
    name: 'Coffee Maker',
    frame: sprites.voyItemCoffee[0],
    infoText:
      'No self-respecting captain would leave on a multi-year journey without coffee!\n\nCoffee speeds up crewmembers who drink it.',
  })

  // register({
  //   startsWith: 'battlerifle',
  //   // crewUpdate: voyageSystems_coffeeMaker.crewUpdate,
  //   onEnter: voyageSystems_battleRifleRack.onEnter,
  //   abbv: 'BR',
  //   name: 'Battle Rifle Rack',
  //   startsWithAmmo: 3,
  //   infoText: 'Battle Rifles - for when you want aliens dead faster (TM)',

  //   extraInfo: (t) => {
  //     if (t.system) {
  //       if (t.system.ammo === 0) {
  //         return 'Is currently empty'
  //       } else {
  //         return (
  //           'Contains ' +
  //           t.system.ammo +
  //           ' battle rifle' +
  //           (t.system.ammo === 1 ? '' : 's')
  //         )
  //       }
  //     }
  //     return '?'
  //   },
  // })

  register({
    startsWith: 'rack',
    allowMultiple: true,
    // crewUpdate: voyageSystems_coffeeMaker.crewUpdate,
    onCreate: voyageSystems_rack.onCreate,
    onEnter: voyageSystems_rack.onEnter,
    abbv: 'R',
    name: 'Equipment Rack',
    startsWithAmmo: 3,
    infoText:
      'General equipment storage. Move on to get or put equipment here.',
    killText: true,
    killImage: true,
    frame: sprites.voyTileSquareRoundedOutline[0],
    tint: voyageColors.purple,

    // extraInfo: (t) => {
    //   if (t.system) {
    //     if (t.system.ammo === 0) {
    //       return 'Is currently empty'
    //     } else {
    //       return (
    //         'Contains ' +
    //         t.system.ammo +
    //         ' battle rifle' +
    //         (t.system.ammo === 1 ? '' : 's')
    //       )
    //     }
    //   }
    //   return '?'
    // },
  })

  register({
    startsWith: 'ejector',
    allowMultiple: true,
    onEnter: voyageSystems_ejector.onEnter,
    abbv: 'EJ',
    name: 'Ejector',
    startsWithAmmo: 3,
    infoText: 'Place items here to jettison them',
  })

  register({
    startsWith: 'ejectrol',
    allowMultiple: true,
    crewUpdate: voyageSystems_ejectrol.crewUpdate,
    abbv: 'EC',
    name: 'Ejector control unit',
    startsWithAmmo: 3,
    infoText: 'Use to jettison items at the ejector.',
  })
  register({
    // Ejection point
    startsWith: 'ejp',
    abbv: 'EJP',
    killText: true,
    hide: true,
  })
  register({
    // Fire point
    startsWith: 'fp',
    abbv: 'FP',
    killText: true,
    hide: true,
  })
}
